import { isWorkspaceV2Path } from 'dashboard/helper/workspaceV2';
import { SETTINGS_NAV_GROUPS } from './settingsNavGroups';
import { safeSettingsReturn, settingsCenterTarget } from './settingsContext';

// O voltar das configurações leva de onde a pessoa veio (decisão do Lucas,
// 22/09/2026). Antes o link principal da barra era "Todas as configurações" e o
// caminho de volta só existia para quem entrava pela central: quem abria uma
// configuração pela conversa, pelo Hub ou pela Central da IA ficava sem volta.
//
// A origem vem do histórico da própria aba (o vue-router guarda o endereço
// anterior em history.state.back), então vale para toda entrada sem precisar
// mexer em cada link. Sem histórico (link aberto direto), vale o return_to; sem
// ele, a central de configurações.

const CENTER_PLACE = 'SETTINGS_CENTER.PLACES.SETTINGS_CENTER';
const ACCOUNT_PATH = /^\/app\/accounts\/(\d+)\/(?:v2\/)?([^/?#]*)([^?#]*)/;
const CONVERSATION_SEGMENTS = [
  'dashboard',
  'conversations',
  'inbox',
  'inbox-view',
  'label',
  'team',
  'custom_view',
  'mentions',
  'unattended',
  'participating',
  'unread',
];
const PLACE_BY_SEGMENT = {
  hub: 'HUB',
  kanban: 'KANBAN',
  tasks: 'TASKS',
  'ai-center': 'AI_CENTER',
  contacts: 'CONTACTS',
  companies: 'CONTACTS',
  reports: 'REPORTS',
  campaigns: 'CAMPAIGNS',
  captain: 'CAPTAIN',
  profile: 'PROFILE',
  'voip-calls': 'CALLS',
};

const parseAccountPath = value => {
  const match = ACCOUNT_PATH.exec(typeof value === 'string' ? value : '');
  if (!match) return null;
  return { accountId: match[1], segment: match[2], rest: match[3] || '' };
};

const pathOnly = value => String(value).split(/[?#]/)[0];

// Telas de configuração ganham o nome que já aparece no menu de configurações.
const settingsItemFor = (router, accountId, subsegment) =>
  SETTINGS_NAV_GROUPS.flatMap(group => group.items).find(item => {
    try {
      const resolved = router.resolve({
        name: item.routeName,
        params: { accountId },
      });
      const parsed = parseAccountPath(resolved.path);
      return (
        parsed?.segment === 'settings' &&
        parsed.rest.split('/')[1] === subsegment
      );
    } catch {
      return false;
    }
  });

export const settingsPlaceLabel = (router, fullPath) => {
  const parsed = parseAccountPath(fullPath);
  if (!parsed) return null;
  const { accountId, segment, rest } = parsed;
  if (CONVERSATION_SEGMENTS.includes(segment))
    return 'SETTINGS_CENTER.PLACES.CONVERSATIONS';
  if (segment === 'kanban') {
    const view = new URL(fullPath, 'https://settings.invalid').searchParams.get(
      'view'
    );
    return view === 'agenda'
      ? 'SETTINGS_CENTER.PLACES.AGENDA'
      : 'SETTINGS_CENTER.PLACES.KANBAN';
  }
  if (segment === 'settings') {
    const subsegment = rest.split('/')[1] || '';
    if (!subsegment || subsegment === 'menu') return CENTER_PLACE;
    return settingsItemFor(router, accountId, subsegment)?.labelKey || null;
  }
  const place = PLACE_BY_SEGMENT[segment];
  return place ? `SETTINGS_CENTER.PLACES.${place}` : null;
};

export const isSettingsScreen = route =>
  /\/(settings|profile)(?:\/|$)/.test(route.path || '');

// A barra de voltar existe nas telas de configuração e nos ajustes abertos pela
// central fora delas (quadro do Kanban em modo configurar). Nunca na própria
// central, que tem o seu voltar no cabeçalho.
export const settingsBarVisible = route =>
  isWorkspaceV2Path(route.path || '') &&
  route.name !== 'settings_menu' &&
  (isSettingsScreen(route) || route.query?.config_center === '1');

export const settingsBackTarget = (router, route) => {
  const accountId = String(route.params?.accountId || '');
  const previous = router.options?.history?.state?.back;
  const parsedPrevious = parseAccountPath(previous);
  // Mesma tela com outra seção (ex.: Kanban → Kanban configurar) não é volta:
  // aí vale a origem guardada no return_to.
  if (
    parsedPrevious &&
    parsedPrevious.accountId === accountId &&
    pathOnly(previous) !== route.path
  ) {
    return {
      mode: 'history',
      to: previous,
      labelKey: settingsPlaceLabel(router, previous),
    };
  }
  const returnTo = safeSettingsReturn(route.query?.return_to, accountId);
  if (returnTo) {
    return {
      mode: 'link',
      to: returnTo,
      labelKey: settingsPlaceLabel(router, returnTo),
    };
  }
  if (route.name === 'settings_menu') return null;
  return {
    mode: 'link',
    to: settingsCenterTarget(route),
    labelKey: CENTER_PLACE,
  };
};

export const isCenterPlace = labelKey => labelKey === CENTER_PLACE;
